// Globe camera (docs/GDD.md §14): orbit + zoom around the globe centre, no pan, damped, with a focus
// animation that swings the camera over a lat/lon (Space re-centres on your capital) and an instant framing.

import { PerspectiveCamera, Quaternion, Vector3 } from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLOBE_RADIUS, DEG_TO_RAD, latLonToVector3 } from '../geo/globeSpace'
import type { Viewport } from '../viewport'

const CAMERA_FOV_DEG = 40
const CAMERA_NEAR = 0.01
/** The closest the camera comes to the globe centre. */
export const MIN_CAMERA_DISTANCE = GLOBE_RADIUS * 1.25
const MAX_CAMERA_DISTANCE = GLOBE_RADIUS * 4
const CAMERA_FAR = MAX_CAMERA_DISTANCE + GLOBE_RADIUS * 2
const DEFAULT_CAMERA_DISTANCE = GLOBE_RADIUS * 3
const DAMPING_FACTOR = 0.08
const ZOOM_SPEED = 0.8
/** Rotate speed at the default altitude; it scales with altitude so the ground moves at a steady rate. */
const ROTATE_SPEED_AT_DEFAULT = 0.9
/** Keeps the orbit off the exact poles, where the look-at basis degenerates. */
const POLAR_MARGIN_RAD = 0.05
const FOCUS_DURATION_SEC = 0.9

type FocusState = { readonly kind: 'idle' } | { readonly kind: 'focusing'; elapsed: number }
const IDLE: FocusState = { kind: 'idle' }
const IDENTITY = new Quaternion()

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

export class GlobeCameraRig {
  readonly camera: PerspectiveCamera

  private readonly controls: OrbitControls
  private focusState: FocusState = IDLE
  private focusDistance = DEFAULT_CAMERA_DISTANCE
  private readonly focusFrom = new Vector3()
  private readonly focusTo = new Vector3()
  private readonly focusRotation = new Quaternion()
  private readonly stepRotation = new Quaternion()

  constructor(domElement: HTMLElement) {
    this.camera = new PerspectiveCamera(CAMERA_FOV_DEG, 1, CAMERA_NEAR, CAMERA_FAR)
    this.camera.position.set(0, 0, DEFAULT_CAMERA_DISTANCE)
    this.controls = new OrbitControls(this.camera, domElement)
    this.controls.enablePan = false
    this.controls.enableDamping = true
    this.controls.dampingFactor = DAMPING_FACTOR
    this.controls.zoomSpeed = ZOOM_SPEED
    this.controls.minDistance = MIN_CAMERA_DISTANCE
    this.controls.maxDistance = MAX_CAMERA_DISTANCE
    this.controls.minPolarAngle = POLAR_MARGIN_RAD
    this.controls.maxPolarAngle = Math.PI - POLAR_MARGIN_RAD
    this.controls.addEventListener('start', this.cancelFocus)
    this.controls.update()
  }

  /** Swings the camera over (lat, lon) at the current distance. Any user drag or zoom cancels it. */
  focus(latDeg: number, lonDeg: number): void {
    this.focusFrom.copy(this.camera.position).normalize()
    latLonToVector3(latDeg, lonDeg, 1, this.focusTo)
    this.focusRotation.setFromUnitVectors(this.focusFrom, this.focusTo)
    this.focusDistance = this.camera.position.length()
    this.focusState = { kind: 'focusing', elapsed: 0 }
  }

  /** Places the camera over (lat, lon) at `distance` from the globe centre (clamped to the zoom range) at once. */
  frame(latDeg: number, lonDeg: number, distance: number): void {
    this.focusState = IDLE
    const clamped = Math.min(MAX_CAMERA_DISTANCE, Math.max(MIN_CAMERA_DISTANCE, distance))
    latLonToVector3(latDeg, lonDeg, clamped, this.camera.position)
    this.controls.update()
  }

  setViewport(viewport: Viewport): void {
    this.camera.aspect = viewport.width / viewport.height
    this.camera.updateProjectionMatrix()
  }

  /** CSS pixels covered by one render unit at unit view depth (for screen-sized glyphs). */
  cssPixelsPerUnitAtUnitDepth(viewport: Viewport): number {
    return viewport.height / (2 * Math.tan((CAMERA_FOV_DEG * DEG_TO_RAD) / 2))
  }

  /** Device pixels covered by one render unit at unit view depth (for world-sized point sprites). */
  pixelsPerUnitAtUnitDepth(viewport: Viewport): number {
    return this.cssPixelsPerUnitAtUnitDepth(viewport) * viewport.pixelRatio
  }

  update(dtSeconds: number): void {
    const state = this.focusState
    if (state.kind === 'focusing') {
      state.elapsed += dtSeconds
      const progress = Math.min(1, state.elapsed / FOCUS_DURATION_SEC)
      this.stepRotation.slerpQuaternions(IDENTITY, this.focusRotation, easeInOutCubic(progress))
      this.camera.position.copy(this.focusFrom).applyQuaternion(this.stepRotation).multiplyScalar(this.focusDistance)
      if (progress === 1) this.focusState = IDLE
    }
    const altitude = this.camera.position.length() - GLOBE_RADIUS
    this.controls.rotateSpeed = (ROTATE_SPEED_AT_DEFAULT * altitude) / (DEFAULT_CAMERA_DISTANCE - GLOBE_RADIUS)
    this.controls.update()
  }

  dispose(): void {
    this.controls.removeEventListener('start', this.cancelFocus)
    this.controls.dispose()
  }

  private readonly cancelFocus = (): void => {
    this.focusState = IDLE
  }
}
